<template>
  <div v-if="booking && allocation" class="grow">
    <div class="flex items-center gap-4">
      <div class="rounded-md bg-[#d8f3ff] px-2 py-1 font-medium">{{ t('return') }}</div>
      <img :src="carProp(allocation.car, 'imageUrl') ?? ''" class="h-7" />
      <div>
        {{ allocation.car.vehicleType.make.name }}
        {{ allocation.car.vehicleType.model }}
      </div>
    </div>

    <Divider />

    <div class="w-fit">
      <ThresholdDatePicker
        :threshold-date="allocation.endDate"
        :initial-date="allocation.returnDate"
        :label="t('returnDate')"
        enable-time-picker
        @date-change="(val) => (returnDate = val)"
      />
    </div>
    <Divider />
    <ReturnCarStatusForm
      :booking="booking"
      :allocation="allocation"
      :is-saving="isSaving"
      @save="handleSaveStatus"
    />

    <Divider />

    <Damages :car-id="allocation.car.id" :booking-id="booking.id" />

    <div class="mt-10 flex justify-end">
      <ButtonNext
        :is-loading="isSaving"
        :disabled="
          isSaving ||
          hasUnsentDamages ||
          typeof allocation.milageEnd !== 'number' ||
          typeof allocation.fuelLevelEnd !== 'number'
        "
        type="submit"
        @click.prevent="handleNext"
      >
        {{ t('next') }}
      </ButtonNext>
    </div>
  </div>
  <div v-else class="flex items-center justify-center py-10">
    <Spinner />
  </div>
</template>

<script setup lang="ts">
import Damages from '@/components/booking-forms/damages/Damages.vue';
import { useUnsentDamages } from '@/hooks/use-unsent-damages';
import { useRoute, useRouter } from 'vue-router';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import { useBooking } from '@/queries/use-bookings';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import ReturnCarStatusForm from '../return/return-car-status/ReturnCarStatusForm.vue';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { useUpdateCar } from '@/queries/use-cars';
import Divider from '@/components/Divider.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { useQueryClient } from '@tanstack/vue-query';
import { carProp } from '@/utils/car-prop';
import ThresholdDatePicker from '@/components/booking-forms/ThresholdDatePicker.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const { data: booking } = useBooking(route.params.bookingId as string);
const allocation = computed(() => {
  return booking.value?.allocations.find((allocation) => allocation.id === route.query.swapFromId);
});
// The returned car's damages must all have arrived before the swap protocol.
const { hasUnsentDamages } = useUnsentDamages(() => allocation.value?.car.id);
const { isPending: isSavingAllocation, mutateAsync: updateAllocation } = useUpdateAllocation();
const { mutateAsync: updateCar, isPending: isSavingCar } = useUpdateCar();
const isSaving = computed(() => isSavingAllocation.value || isSavingCar.value);

const handleSaveStatus = async (values: { milage: number; fuelLevel: number }) => {
  if (!allocation.value || !booking.value) return;
  await updateAllocation({
    id: allocation.value.id,
    allocation: {
      milageEnd: values.milage,
      fuelLevelEnd: values.fuelLevel,
    },
  });
  await queryClient.invalidateQueries({
    queryKey: ['bookings', booking.value.id],
  });
  await updateCar({
    id: allocation.value.car.id,
    car: values,
  });
};

const returnDate = ref<string>();

const handleNext = async () => {
  if (!allocation.value) return;
  await updateAllocation({
    id: allocation.value.id,
    allocation: {
      returnDate: returnDate.value ? new Date(returnDate.value) : undefined,
    },
  });
  router.push({
    name: route.query.swapToId ? 'swapHandoverStatus' : 'swapProtocol',
    params: {
      bookingId: route.params.bookingId,
    },
    query: {
      swapFromId: route.query.swapFromId as string,
      swapToId: route.query.swapToId as string,
    },
  });
};
</script>
<i18n lang="json">
{
  "en": {
    "back": "back to customer bookings overview",
    "return": "Return",
    "returnDate": "Return date"
  },
  "de": {
    "back": "Zurück zur Buchungsübersicht",
    "return": "Rückgabe",
    "returnDate": "Rückgabedatum"
  },
  "es": {
    "back": "volver a la vista general de reservas del cliente",
    "return": "Devolución",
    "returnDate": "Fecha de devolución"
  }
}
</i18n>
